import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { getReporteLiquidacion } from '../../services/data';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { toMoney } from '../../utils/numbers';
import { fechaCorta } from '../finanzas/estados';
import PeriodoFiltro, { usePeriodo } from '../cierres/PeriodoFiltro';
import { claseLiquidacion } from '../cierres/LiquidacionSeccion';
import CierreDetalleModal from '../cierres/CierreDetalleModal';
import { exportReporteLiquidacionPdf } from '../../pdf/reporteLiquidacion';
import '../finanzas/ui.css';
import '../proveedores/Proveedores.css';
import './Reportes.css';

const ESTADOS = [['', 'Todos'], ['Pendiente', 'Pendiente'], ['Parcial', 'Parcial'], ['Liquidado', 'Liquidado']];
const turno = (t) => ({ dia: 'Día', noche: 'Noche', dia_completo: 'Día completo' }[t] || '—');

// Reporte de Cierres y Liquidación de Efectivo: consolidado del periodo y detalle por cierre.
export default function ReporteLiquidacion() {
  const { sucursalId, nombreSucursal, me } = useSucursalActiva();
  const per = usePeriodo('hoy');
  const [estado, setEstado] = useState('');
  const [cajero, setCajero] = useState('');
  const [rep, setRep] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null);

  const cargar = useCallback(async () => {
    if (!sucursalId) return;
    setCargando(true);
    try { setRep(await getReporteLiquidacion({ desde: per.desde, hasta: per.hasta, sucursalId, estado, cajero })); }
    catch (e) { Swal.fire('Error', e.message || 'No se pudo generar el reporte.', 'error'); }
    finally { setCargando(false); }
  }, [sucursalId, per.desde, per.hasta, estado, cajero]);
  useEffect(() => { cargar(); }, [cargar]);

  const nombreSuc = nombreSucursal;
  const porDia = useMemo(() => {
    const m = new Map();
    (rep?.cierres || []).forEach((c) => { if (!m.has(c.fecha)) m.set(c.fecha, []); m.get(c.fecha).push(c); });
    return [...m.entries()];
  }, [rep]);
  const r = rep?.resumen;
  const fila = (label, v, fuerte) => <tr><td>{fuerte ? <b>{label}</b> : label}</td><td className="num">{fuerte ? <b>{toMoney(v)}</b> : toMoney(v)}</td></tr>;

  return (
    <div className="prov-shell">
      <header className="prov-header">
        <div><h1>Reporte de Cierres y Liquidación</h1><p style={{ margin: 0, color: 'var(--ink-muted)' }}>Qué produjo cada turno y qué pasó después con el efectivo.</p></div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="fx-btn" disabled={!rep || cargando}
          onClick={() => exportReporteLiquidacionPdf(rep, { sucursal: nombreSuc, generadoPor: me?.username, estado, cajero }, { imprimir: true })}>Imprimir</button>
        <button className="fx-btn fx-btn--primary" disabled={!rep || cargando}
          onClick={() => exportReporteLiquidacionPdf(rep, { sucursal: nombreSuc, generadoPor: me?.username, estado, cajero })}>Descargar PDF</button>
        </div>
      </header>

      <PeriodoFiltro p={per}>
        <label>Liquidación
          <select value={estado} onChange={(e) => setEstado(e.target.value)}>{ESTADOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </label>
        <label>Cajero
          <select value={cajero} onChange={(e) => setCajero(e.target.value)}>
            <option value="">Todos</option>{(rep?.cajeros || []).map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
      </PeriodoFiltro>

      {cargando && <p className="fx-empty">Generando…</p>}
      {!cargando && r && <>
        <section className="rep-resumen">
          <div className="fx-card"><h2>Ventas</h2><table className="fx-table"><tbody>
            {fila('Ventas totales', r.ventasTotales, true)}{fila('Efectivo', r.ventasEfectivo)}{fila('Tarjeta', r.ventasTarjeta)}{fila('PedidosYa', r.ventasPedidosYa)}
            {fila('Compras', r.compras)}{fila('Gastos', r.gastos)}</tbody></table></div>
          <div className="fx-card"><h2>Efectivo y liquidación</h2><table className="fx-table"><tbody>
            {fila('Efectivo disponible para liquidar', r.efectivoGenerado, true)}{fila('Depositado', r.depositado)}{fila('Entrega de efectivo', r.entregadoDuena)}
            {fila('Pagado a proveedores', r.pagadoProveedores)}{fila('Otros destinos', r.otrosDestinos)}{fila('Reservado para aperturas', r.reservado)}
            {fila('Pendiente de liquidar', r.pendienteLiquidar, true)}</tbody></table></div>
          <div className="fx-card"><h2>Cuadre de caja</h2><table className="fx-table"><tbody>
            <tr><td>Cierres</td><td className="num"><b>{r.cierres}</b></td></tr>{fila('Faltantes', r.faltantes)}{fila('Sobrantes', r.sobrantes)}</tbody></table>
            <p className="fx-empty">El faltante/sobrante del cajero y el efectivo pendiente de liquidar son conceptos distintos.</p></div>
        </section>

        {!rep.cierres.length && <p className="fx-card fx-empty">No hay cierres en este periodo con los filtros elegidos.</p>}
        {porDia.map(([fecha, cierres]) => (
          <section key={fecha} className="rep-dia">
            {per.desde !== per.hasta && <h2 className="rep-dia-titulo">Día {fechaCorta(fecha)}</h2>}
            {cierres.map((c) => {
              const L = c.liquidacion;
              return (
                <article key={c.cierreId} className="fx-card rep-cierre">
                  <header>
                    <div><h3>Cierre {fechaCorta(c.fecha)} – Turno {turno(c.turnoId)} · {c.sucursal}</h3><p>Cajero: {c.cajero}</p></div>
                    <div className="rep-cierre-acc"><span className={`fx-badge fx-badge--${claseLiquidacion[L.estado]}`}>{L.estado}</span>
                      <button type="button" className="fx-link" onClick={() => setDetalle(c.cierreId)}>Ver cierre</button></div>
                  </header>
                  <div className="rep-cifras">
                    <div><span>Ventas en efectivo</span><b>{c.ventas.efectivo == null ? 'Pendiente' : toMoney(c.ventas.efectivo)}</b></div><div><span>Tarjeta</span><b>{toMoney(c.ventas.tarjeta)}</b></div>
                    <div><span>PedidosYa</span><b>{toMoney(c.ventas.pedidosYa)}</b></div><div><span>Compras</span><b>{toMoney(c.compras)}</b></div>
                    <div><span>Gastos</span><b>{toMoney(c.gastos)}</b></div>
                    <div><span>Efectivo esperado</span><b>{c.efectivoEsperado == null ? 'Pendiente' : toMoney(c.efectivoEsperado)}</b></div>
                    {c.efectivoReportado != null && <><div><span>Efectivo contado</span><b>{toMoney(c.efectivoReportado)}</b></div>
                      <div><span>Diferencia de caja</span><b style={{ color: c.diferencia < 0 ? 'var(--danger)' : undefined }}>{c.diferencia == null ? 'Pendiente' : toMoney(c.diferencia)}</b></div></>}
                    {c.efectivoReportado == null && <div><span>Diferencia de ventas</span><b>{c.diferenciaVentas == null ? 'Pendiente' : toMoney(c.diferenciaVentas)}</b></div>}
                  </div>
                  <table className="fx-table fx-table--stack">
                    <thead><tr><th>Fecha</th><th>Destino</th><th>Detalle</th><th className="num">Monto</th></tr></thead>
                    <tbody>{L.movimientos.length ? L.movimientos.map((m, i) => (
                      <tr key={i}><td data-label="Fecha">{fechaCorta(m.fechaAt)}</td><td data-label="Destino">{m.tipoNombre}</td><td data-label="Detalle">{m.detalle}</td>
                        <td data-label="Monto" className="num">{toMoney(m.monto)}</td></tr>))
                      : <tr><td colSpan={4} className="fx-empty">Sin movimientos de liquidación.</td></tr>}</tbody>
                  </table>
                  <p className="rep-total">Total aplicado <b>{toMoney(L.aplicado)}</b> · Pendiente <b>{toMoney(L.pendiente)}</b></p>
                </article>);
            })}
          </section>
        ))}
      </>}
      {detalle && <CierreDetalleModal cierreId={detalle} onClose={() => { setDetalle(null); cargar(); }} onChanged={cargar} />}
    </div>
  );
}
